import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Container,
    Typography,
    Divider,
    TextField,
    Menu,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Autocomplete,
    Chip,
    InputAdornment,
    CircularProgress,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

import AIAgentsTable from "./AIAgentsTable"; // This will use the updated version below
import { useAuth } from "../../helpers/AuthProvider";
import queryGraphql from "../../helpers/ApiInterface";
import MainDrawer from "../MainDrawer";
import MiniBot from "../MiniBot";

interface AIAgentOwnerInfo {
    id: string;
    name: string;
    email?: string;
    googleProfilePicture?: string | null;
}

// Updated interface to include cloudProvider
interface AIAgent {
    id: string;
    projectId: string;
    region: string;
    status: string;
    environment?: string;
    imageUrl?: string;
    gpu: string | number;
    applicationName: string;
    modelName: string;
    updatedAt: string;
    createdAt: string;
    repoUrl?: string;
    agentName: string; 
    cloudProvider: string; // New field
    aIAgentOwner?: AIAgentOwnerInfo | null;
    reasonForRejection?: string;
  
}

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: keyof AIAgent | string;
    groupBy: string;
}

let searchOptionsMasterList: SearchOptions[] = [];

const AIAgent: React.FC = () => {
    const [allAIAgents, setAllAIAgents] = useState<AIAgent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);

    // Updated column filters to include cloudProvider
    const initialColumnFilters: Record<string, boolean> = {
        agentName: true,
        projectId: true,
        applicationName: true,
        cloudProvider: true, // Added new field
        gpu: true,
        modelName: true,
        aIAgentOwner: true,
        createdAt: true,
    };
    const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);
    const [tempColumnFilters, setTempColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await queryGraphql(`
                    query GetSolutionAIAgents {
                      getSolutionAIAgents {
                        id
                        name
                        solutionType
                        cloudProvider
                        environment
                        costLabelPairs {
                          cost
                          label
                        }
                        status
                        details {
                          aiAgent {
                            projectId
                            region
                            imageUrl
                            gpu
                            modelName
                            modelTemperature
                            modelMaxTokens
                            modelStreaming
                            repoUrl
                          }
                        }
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          name
                          email
                          id
                          googleProfilePicture
                        }
                        application {
                          applicationName
                          id
                        }
                      }
                    }
                `);

                const agentsData = response.data?.getSolutionAIAgents || response.getSolutionAIAgents || [];

                if (agentsData) {
                    const processedAgents = agentsData.map((solution: any): AIAgent => ({
                        id: solution.id,
                        projectId: solution.details?.aiAgent?.projectId || "N/A",
                        agentName: solution.name || "Unknown Agent",
                        region: solution.details?.aiAgent?.region || "N/A",
                        status: (solution.status || 'pending').toLowerCase(),
                        environment: solution.environment,
                        cloudProvider: solution.cloudProvider || "N/A", // Mapped new field
                        imageUrl: solution.details?.aiAgent?.imageUrl,
                        gpu: solution.details?.aiAgent?.gpu === null || solution.details?.aiAgent?.gpu === undefined ? "0" : String(solution.details?.aiAgent?.gpu),
                        applicationName: solution.application?.applicationName || "N/A",
                        modelName: solution.details?.aiAgent?.modelName || "N/A",
                        repoUrl: solution.details?.aiAgent?.repoUrl,
                        aIAgentOwner: solution.solutionOwner ? {
                            id: solution.solutionOwner.id,
                            name: solution.solutionOwner.name,
                            email: solution.solutionOwner.email,
                            googleProfilePicture: solution.solutionOwner.googleProfilePicture,
                        } : null,
                        updatedAt: solution.updatedAt,
                        createdAt: solution.createdAt,
                        reasonForRejection: solution.reasonForRejection,
                    }));
                    setAllAIAgents(processedAgents);

                    searchOptionsMasterList = processedAgents
                        .map((pa: AIAgent) => [
                            { category: "agentName", groupBy: "AGENT NAME", chipLabel: `Agent: ${pa.agentName}`, dropdownLabel: pa.agentName },
                            { category: "projectId", groupBy: "PROJECT ID", chipLabel: `Project ID: ${pa.projectId}`, dropdownLabel: pa.projectId },
                            { category: "applicationName", groupBy: "APPLICATION NAME", chipLabel: `App: ${pa.applicationName}`, dropdownLabel: pa.applicationName },
                            { category: "cloudProvider", groupBy: "CLOUD PROVIDER", chipLabel: `Provider: ${pa.cloudProvider}`, dropdownLabel: pa.cloudProvider },
                            { category: "modelName", groupBy: "MODEL NAME", chipLabel: `Model: ${pa.modelName}`, dropdownLabel: pa.modelName },
                            { category: "region", groupBy: "REGION", chipLabel: `Region: ${pa.region}`, dropdownLabel: pa.region },
                            { category: "status", groupBy: "STATUS", chipLabel: `Status: ${pa.status}`, dropdownLabel: pa.status.charAt(0).toUpperCase() + pa.status.slice(1) },
                            ...(pa.aIAgentOwner ? [{ category: "aIAgentOwner.name" as any, groupBy: "RESOURCE OWNER", chipLabel: `Owner: ${pa.aIAgentOwner.name}`, dropdownLabel: pa.aIAgentOwner.name }] : []),
                            
                        ])
                        .flat();

                    searchOptionsMasterList = searchOptionsMasterList.filter(
                        (obj, index, self) =>
                            obj.dropdownLabel &&
                            index === self.findIndex((t) =>
                                t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
                            )
                    );
                    searchOptionsMasterList.sort((a, b) => {
                        const groupA = a.groupBy.toUpperCase();
                        const groupB = b.groupBy.toUpperCase();
                        if (groupA > groupB) return 1;
                        if (groupA < groupB) return -1;
                        const labelA = a.dropdownLabel.toUpperCase();
                        const labelB = b.dropdownLabel.toUpperCase();
                        if (labelA > labelB) return 1;
                        if (labelA < labelB) return -1;
                        return 0;
                    });
                }
            } catch (error) {
                console.error("Error fetching AI Agents:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setTempColumnFilters(columnFilters);
        setColumnFilterAnchorEl(event.currentTarget);
    };

    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const handleTempColumnFilterChange = (column: string) => {
        setTempColumnFilters((prevFilters) => ({
            ...prevFilters,
            [column]: !prevFilters[column],
        }));
    };

    const handleApplyColumnFilters = () => {
        setColumnFilters(tempColumnFilters);
        handleColumnFilterClose();
    };

    const getDisplayLabelForKey = (key: string): string => {
        if (key === 'aIAgentOwner') return 'Resource Owner';
        if (key === 'createdAt') return 'Added On';
        if (key === 'agentName') return 'Agent Name';
        if (key === 'cloudProvider') return 'Cloud Provider';
        const result = key.replace(/([A-Z])/g, ' $1');
        return result.charAt(0).toUpperCase() + result.slice(1).trim();
    };


    const globallyFilteredAgents =
        searchQuery.length === 0
            ? allAIAgents
            : allAIAgents.filter((agent: AIAgent) => {
                return searchQuery.every(queryParam => {
                    let agentValue: any;
                    if (queryParam.category === "aIAgentOwner.name") {
                        agentValue = agent.aIAgentOwner?.name;
                    } else {
                        agentValue = agent[queryParam.category as keyof AIAgent];
                    }
                    if (agentValue === null || agentValue === undefined) return false;
                    return String(agentValue).toLowerCase().includes(queryParam.dropdownLabel.toLowerCase());
                });
            });

    const pendingAgents = globallyFilteredAgents.filter(agent => agent.status === 'pending');
    const activeAgents = globallyFilteredAgents.filter(agent => agent.status === 'active' || agent.status === 'approved'); // Including 'approved' as active
    const rejectedAgents = globallyFilteredAgents.filter(agent => agent.status === 'rejected');

    const totalAgentsOverall = allAIAgents.length;
    const totalMatchingSearch = globallyFilteredAgents.length;

    const canAddAIAgent = true;

    const handleAddAIAgent = () => {
        navigate("/aisolutions/aiagent/add");
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex" }}>
                <MainDrawer index={"aisolutions-aiagent"} />
                <Box component="main" sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"aisolutions-aiagent"} />
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: "100vh",
                }}
            >
                <MiniBot />
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={{xs:1.5, sm:2}} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: "space-between",
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                mx:2
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
                                AI Agents
                            </Typography>
                            <Box sx={{display:'flex', justifyContent:'center'}}>
                                <Box>
                            {canAddAIAgent && (
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: "black", color: "white", padding: "8px 24px",
                                        fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
                                        '&:hover': { backgroundColor: '#333333' }
                                    }}
                                    onClick={handleAddAIAgent}
                                    disabled={!user.roles.includes("cluster_owner") && !user.roles.includes("viewer")}
                                >
                                    Add New AI Agent
                                </Button>
                                
                            )}
                            </Box>
                            <IconButton
                                                                  aria-label="Help" 
                                                                  onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                                                  sx={{
                                                                    padding: 0, 
                                                                    border: 'none',  
                                                                    ml:2,
                                                                    '&:hover': {
                                                                      
                                                                      backgroundColor: 'action.hover', 
                                                                    },
                                                                    
                                                                  }}
                                                                >
                                                                  <Box
                                                                    component="img"
                                                                    src="/assets/Help.svg" 
                                                                    alt="" 
                                                                    sx={{
                                                                      width: 40,  
                                                                      height: 40,
                                                                      display: 'block', 
                                                                    }}
                                                                  />
                                                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: 'wrap' }}>
                            <Autocomplete
                                multiple
                                options={searchOptionsMasterList}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                value={searchQuery}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} sx={{ fontFamily: "Gilroy-Regular" }} />
                                    ))
                                }
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal as SearchOptions[]);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by agent name, project, status, etc."
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        sx={{ minWidth: { xs: '100%', sm: 300, md: 500 }, flexGrow: 1 }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start" sx={{ pl: 0.5 }}>
                                                        <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px" }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "44px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                                PaperComponent={(props) => <Paper elevation={3} {...props} />}
                            />
                            <Button
                                sx={{
                                
                                    border: "1px solid #bbb",
                                    borderRadius: "10px",
                                    height: "45px",
                                    width: "45px",
                                }}
                                onClick={handleColumnFilterClick}
                            >
                                <img src="/assets/pause.svg" alt="Filter Icon" style={{ width: "16px" }} />
                            </Button>
                            <Menu
                                anchorEl={columnFilterAnchorEl}
                                open={Boolean(columnFilterAnchorEl)}
                                onClose={handleColumnFilterClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                PaperProps={{ sx: { border: '1px solid #E0E0E0', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1 } }}
                            >
                                <Box sx={{ p: "16px", minWidth: 240, maxWidth: 280 }}>
                                    <Typography sx={{ fontFamily: "Gilroy-Bold", fontSize: "16px", fontWeight: 700, color: "#1F2937", marginBottom: "16px" }}>
                                        TABLE COLUMNS
                                    </Typography>
                                    <FormGroup>
                                        {Object.entries(tempColumnFilters).map(([key, value]) => (
                                            <FormControlLabel
                                                key={key}
                                                control={
                                                    <Checkbox
                                                        checked={value}
                                                        onChange={() => handleTempColumnFilterChange(key)}
                                                        size="small"
                                                        sx={{ color: "#4B5563", "&.Mui-checked": { color: "#000000" }, "& .MuiSvgIcon-root": { fontSize: "22px" }, py: '4px', mr: '8px' }}
                                                    />
                                                }
                                                label={<Typography sx={{ fontFamily: "Gilroy-Medium", fontSize: '17px', color: '#1F2937', lineHeight: '1.5', py: '1vh' }}>{getDisplayLabelForKey(key)}</Typography>}
                                                sx={{ marginBottom: '4px' }}
                                            />
                                        ))}
                                    </FormGroup>
                                    <Button
                                        variant="contained" fullWidth onClick={handleApplyColumnFilters}
                                        sx={{ fontFamily: "Gilroy-Bold", fontSize: "14px", fontWeight: 700, color: "#FFFFFF", backgroundColor: "#000000", textTransform: 'uppercase', borderRadius: "6px", padding: "10px 0", marginTop: "20px", '&:hover': { backgroundColor: "#333333" } }}
                                    >
                                        Apply
                                    </Button>
                                </Box>
                            </Menu>
                        </Box>
                    </Box>

                    <Box m={{xs: 1, sm:2}} sx={{minHeight:'70vh'}}>
                        {totalAgentsOverall > 0 && totalMatchingSearch > 0 &&(
                            <>
                        <Accordion
                            defaultExpanded={pendingAgents.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={pendingAgents.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Pending Agents ({pendingAgents.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {pendingAgents.length > 0 ? (
                                    <AIAgentsTable
                                        agents={pendingAgents}
                                        columnFilters={columnFilters}
                                        statusType="pending"
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No pending AI Agents match your filters.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={activeAgents.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={activeAgents.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Active Agents ({activeAgents.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {activeAgents.length > 0 ? (
                                    <AIAgentsTable
                                        agents={activeAgents}
                                        columnFilters={columnFilters}
                                        statusType="active"
                                    />
                                ) : (
                                     <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No active AI Agents match your filters.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={rejectedAgents.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={rejectedAgents.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Rejected Agents ({rejectedAgents.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {rejectedAgents.length > 0 ? (
                                    <AIAgentsTable
                                        agents={rejectedAgents}
                                        columnFilters={columnFilters}
                                        statusType="rejected"
                                    />
                                ) : (
                                     <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No rejected AI Agents match your filters.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                        </>)}
                         {totalAgentsOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor:"#FFFFFF", borderRadius:"8px" }}>
                                <img src="/assets/inbox-icon.svg" alt="No AI Agents Match" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No AI Agents match your search
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
                                    Try adjusting your search terms or filters.
                                </Typography>
                            </Box>
                        )}

                        {totalAgentsOverall === 0 && !isLoading && (
                             <Box
                                display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                                sx={{
                                    textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
                                    backgroundColor: '#FFFFFF', borderRadius: "8px",
                                    boxShadow: "0px 1px 3px rgba(0,0,0,0.05)",
                                   // height:"70vh"
                                 
                                }}
                            >
                                <img src="/assets/inbox-icon.svg" alt="No AI Agents" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No AI Agent are added yet
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
                                    Click the button above to add your first AI Agent Cluster.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default AIAgent;